<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core';
import { maxValue, minValue, required } from '@vuelidate/validators';
import { ref, computed, type PropType, reactive, onMounted, watch } from 'vue';

import { trimNumberToPrecision } from '@exchange/helpers/number-transform-helper';
import type { PerpsPosition } from '@exchange/libs/account/service/src/lib/wss-account-messages';
import { marketService } from '@exchange/libs/market/service/src/index';
import { modalVariant, type ModalVariant } from '@exchange/libs/modals/src';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import OrderSizeInput from '@exchange/libs/order/order-form/ui/src/lib/components/OrderSizeInput.vue';
import useOrderTypes from '@exchange/libs/order/order-form/ui/src/lib/OrderTypes';
import useOrderFormPrice from '@exchange/libs/order/order-form/ui/src/lib/useOrderFormPrice';
import { OrderSide } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';

const props = defineProps({
  position: { type: Object as PropType<PerpsPosition>, required: true },
  variant: { type: String as PropType<ModalVariant>, default: modalVariant.dark },
});

const emit = defineEmits<(e: 'close' | 'submit') => void>();

const { getMarket } = marketService;

const { market: marketType, limit: limitType } = useOrderTypes();
const activeType = ref(marketType.type);

const market = computed(() => getMarket(props.position.instrument_code));

const { marketOrderPrice } = useOrderFormPrice(
  market,
  computed(() => activeType.value === marketType.type),
);

const positionSize = computed(() => `${props.position.open_volume} ${market.value?.base?.code}`);

const limitPriceValue = ref<string | null>(null);
const amountValue = ref<string | null>(null);
const totalValue = ref<string | null>(null);
const sliderPercentage = ref(100); // Default to 100%

// Validation rules
const validationRules = computed(() => ({
  amountValue: {
    required,
    minAmount: minValue(0),
    maxAmount: maxValue(props.position.open_volume),
    $autoDirty: true,
  },
  limitPriceValue:
    activeType.value === limitType.type
      ? {
          required,
          minPrice: minValue(0),
          $autoDirty: true,
        }
      : {},
}));

const validationState = reactive({
  amountValue,
  limitPriceValue,
});

const v = useVuelidate(validationRules, validationState);

// Input handlers
const onAmountInput = (value: string) => {
  amountValue.value = value;
};

const onLimitPriceInput = (value: string) => {
  limitPriceValue.value = value;
};

const onTotalInput = (value: string) => {
  totalValue.value = value;
};

const marketPrice = computed(() => '1');

// Set initial values immediately when component mounts
onMounted(() => {
  // Set initial amount value
  amountValue.value = props.position.open_volume.toString();

  // If it's a limit order, maybe set an initial price
  if (activeType.value === limitType.type) {
    limitPriceValue.value = marketPrice.value;
  }

  // Calculate total value based on amount and price
  if (amountValue.value && marketPrice.value) {
    totalValue.value = (Number(amountValue.value) * Number(marketPrice.value)).toString();
  }
});

// Determine order side based on position direction
const orderSide = computed(() => (props.position.direction === 'LONG' ? OrderSide.SELL : OrderSide.BUY));

const isSubmitting = ref(false);

const onClosePosition = async () => {
  if (v.value.$invalid) {
    v.value.$touch();
    return;
  }

  try {
    // Set up order form service with our values
    orderformService.setSide(orderSide.value);
    orderformService.setType(activeType.value);

    isSubmitting.value = true;

    const payload = await orderformService.getPostOrderPayload({
      instrument: props.position.instrument_code,
      amount: amountValue.value,
      limitPrice: activeType.value === limitType.type ? limitPriceValue.value : null,
      stopPrice: null,
      minPrice: '0',
      maxPrice: '999999999',
    });

    // Submit the order
    const response = await orderformService.postOrder.rest(payload);

    if (response === null) {
      throw new Error('Server null error');
    }

    orderformService.handleOrderCreatedSuccess();
    emit('submit');
  } catch (error) {
    const { data } = error;
    orderformService.handleOrderCreationFailed(data?.error ?? '');
  } finally {
    isSubmitting.value = false;
  }
};

// Add computed for price that handles both market and limit orders
const displayPrice = computed(() => {
  if (activeType.value === marketType.type) {
    return marketOrderPrice.value;
  }
  return limitPriceValue.value;
});

// Update slider logic for percentages
const onSliderChange = (percentage: number) => {
  sliderPercentage.value = percentage;
  const amount = (Number(props.position.open_volume) * percentage) / 100;
  amountValue.value = amount.toString();
};

const valueFormatter = (value: number) => `${value.toFixed(2)}%`;

const { currencies, defaultFiatCurrency } = currencyService;

const showTooltips = ref({
  amountValue: false,
  totalValue: false,
});

const resetShowTooltips = () => {
  showTooltips.value = {
    amountValue: false,
    totalValue: false,
  };
};

// Update tooltipContent to handle initial state better
const tooltipContent = (inValue: string | null, inputName: 'amountValue' | 'totalValue') => {
  // Use either the passed value or the current ref value
  const value = Number(inValue ?? (inputName === 'amountValue' ? amountValue.value : totalValue.value));

  if (!currencies.value || !market.value) {
    return null;
  }

  const defaultFiatCurrencyPrecision = currencies.value[defaultFiatCurrency.value]?.precision ?? CONSTANTS.PRECISION.DEFAULT_FIAT;
  const currencyCode = market.value[inputName === 'amountValue' ? 'base' : 'quote'].code;
  const inputCurrencyInFiatValue = currencies.value[currencyCode]?.defaultFiatValue || 0;

  let fiatValue = value * inputCurrencyInFiatValue;
  fiatValue = trimNumberToPrecision(defaultFiatCurrencyPrecision)(fiatValue);

  return {
    content: `${fiatValue} ${defaultFiatCurrency.value}`,
    theme: 'tooltip-fiat',
    shown: showTooltips.value[inputName],
    placement: 'top-end',
    distance: 0.4,
  };
};

const onInputFocus = ($event: FocusEvent) => {
  if (!$event.target || ($event.target && !('name' in $event.target))) {
    return;
  }

  const { name } = $event.target;
  showTooltips.value[name as keyof typeof showTooltips.value] = true;
};

const onInputBlur = ($event: FocusEvent) => {
  resetShowTooltips();
};

// Update watch for amount/price changes with proper precision handling
watch(amountValue, (newAmount) => {
  if (newAmount && displayPrice.value) {
    const amount = trimNumberToPrecision(market.value?.amountPrecision ?? 8)(Number(newAmount));
    const price = Number(displayPrice.value);
    // Apply market precision to the total calculation
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price));
    totalValue.value = total.toString();
  }
});

// Similar fix for other watchers that calculate total
watch(limitPriceValue, (newPrice) => {
  if (activeType.value === limitType.type && amountValue.value && newPrice) {
    const amount = Number(amountValue.value);
    const price = Number(newPrice);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price));
    totalValue.value = total.toString();
  }
});

watch(marketOrderPrice, (newPrice) => {
  if (activeType.value === marketType.type && amountValue.value && newPrice) {
    const amount = Number(amountValue.value);
    const price = Number(newPrice);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price));
    totalValue.value = total.toString();
  }
});

// Also watch for price type changes
watch(activeType, () => {
  // Recalculate total when switching between market/limit
  if (amountValue.value && displayPrice.value) {
    const amount = trimNumberToPrecision(market.value?.amountPrecision ?? 8)(Number(amountValue.value));
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * Number(displayPrice.value));
    totalValue.value = total.toString();
  }
});

// Watch limit price changes to update total only
watch(limitPriceValue, (newPrice) => {
  if (activeType.value === limitType.type && amountValue.value && newPrice) {
    // Keep the original amount, only update total
    const amount = Number(amountValue.value);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * Number(newPrice));
    totalValue.value = total.toString();
  }
});

// Add watch for marketOrderPrice changes
watch(marketOrderPrice, (newPrice) => {
  if (activeType.value === marketType.type && amountValue.value && newPrice) {
    const amount = Number(amountValue.value);
    const price = Number(newPrice);
    const total = trimNumberToPrecision(market.value?.marketPrecision ?? 8)(amount * price);
    totalValue.value = total.toString();
  }
});
</script>

<template>
  <x-modal
    class="close-position"
    :can-close="true"
    :wide="true"
    :variant="variant"
    title="Close position"
    @close="emit('close')"
  >
    <template #content>
      <div class="close-position__content">
        <div class="close-position__info">
          <div class="position-header">
            <div class="position-header__left">
              <x-logo
                class="position-header__icon"
                :name="market?.base?.code"
                type="crypto"
              />
              <div class="position-header__details">
                <div class="position-header__title">
                  <span
                    class="position-header__direction"
                    :class="{
                      'position-header__direction--long': position.direction === 'LONG',
                      'position-header__direction--short': position.direction === 'SHORT',
                    }"
                  >
                    {{ position.direction === 'LONG' ? $t('fundamentals.orders.side.long') : $t('fundamentals.orders.side.short') }}
                  </span>
                  <span class="position-header__symbol">{{ market?.name }}</span>
                  <ot-lozenge
                    :text="$t('fundamentals.perpetual.short')"
                    :filled="false"
                  />
                </div>
                <span class="position-header__size">
                  {{ positionSize }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div class="close-position__form">
          <div class="order-type-switcher">
            <x-button
              class="order-type-switcher__button order-type-switcher__button--left"
              testid="order-type-limit"
              size="xs"
              rounded="sm"
              :class="{ 'order-type-switcher__button--underlined': activeType === limitType.type, 'order-type-switcher__button--base': activeType !== limitType.type }"
              :variant="activeType === limitType.type ? 'underlined-primary' : 'base'"
              text="Limit"
              @click="activeType = limitType.type"
            />
            <x-button
              class="order-type-switcher__button order-type-switcher__button--right"
              testid="order-type-market"
              size="xs"
              rounded="sm"
              :class="{ 'order-type-switcher__button--underlined': activeType === marketType.type, 'order-type-switcher__button--base': activeType !== marketType.type }"
              :variant="activeType === marketType.type ? 'underlined-primary' : 'base'"
              text="Market"
              @click="activeType = marketType.type"
            />
          </div>

          <!-- Only show price input for limit orders -->
          <div
            v-if="activeType === limitType.type"
            class="close-position-form__input-wrapper"
          >
            <ot-input-error-wrapper
              :no-message-placeholder="true"
              :error="v.limitPriceValue"
              :error-messages="{ required: 'Price is required' }"
            >
              <x-input-number
                v-model:value="limitPriceValue"
                name="limitPriceValue"
                label="Price"
                :state="v.limitPriceValue.$error ? 'error' : 'default'"
                variant="dark"
                :compact="true"
                :precision="market?.marketPrecision"
                :currency="market?.quote?.code"
                :lpignore="true"
                testid="close-limit-price"
                @update:value="onLimitPriceInput"
              />
            </ot-input-error-wrapper>
          </div>

          <div class="close-position-form__input-wrapper">
            <ot-input-error-wrapper
              :error="v.amountValue"
              :error-messages="{
                required: 'Amount is required',
                minAmount: 'Amount must be greater than 0',
                maxAmount: 'Amount cannot exceed position size',
              }"
            >
              <order-size-input
                v-model:amount-value="amountValue"
                v-model:total-value="totalValue"
                :autofocus="false"
                :amount-tooltip="tooltipContent(amountValue, 'amountValue')"
                :total-tooltip="tooltipContent(totalValue, 'totalValue')"
                :market="market"
                :input-state="v.amountValue.$error ? 'error' : 'default'"
                :order-side="orderSide"
                @update:amount-value="onAmountInput"
                @update:total-value="onTotalInput"
                @input-blur="onInputBlur"
                @input-focus="onInputFocus"
              />
            </ot-input-error-wrapper>
          </div>

          <x-slider
            class="close-position-form__slider"
            :model-value="sliderPercentage"
            :color-variant="'primary'"
            :min="0"
            :max="100"
            :marks="[0, 25, 50, 75, 100]"
            :interval="25"
            :step-style="{ width: '6px', height: '6px', top: '-1px', left: '-1px' }"
            :duration="0.2"
            :tooltip-formatter="valueFormatter"
            :disabled="false"
            :drag-on-click="true"
            @change="onSliderChange"
          />

          <x-button
            class="close-position-form__submit"
            :disabled="v.$invalid || isSubmitting"
            :loading="isSubmitting"
            text="Close Position"
            variant="primary"
            :block="true"
            size="md"
            testid="submit-close-position"
            @click="onClosePosition"
          />
        </div>
      </div>
    </template>
  </x-modal>
</template>

<style lang="scss" scoped>
/* stylelint-disable */

.close-position {
  &__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 0 14px;
  }

  &__info {
    text-align: left;
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}

.position-header {
  display: flex;
  align-items: center;
  padding: 12px 0;
  background: rgb(var(--v-theme-elevation-1));
  border-radius: 4px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__icon {
    width: 32px;
    height: 32px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__direction {
    font-weight: 500;

    &--long {
      color: rgb(var(--v-theme-success));
    }

    &--short {
      color: rgb(var(--v-theme-error));
    }
  }

  &__symbol {
    color: rgb(var(--v-theme-text-primary));
    font-weight: 500;
  }

  &__size {
    color: rgb(var(--v-theme-text-primary));
    font-size: var(--font-size-lg);
  }
}

.order-type-switcher {
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
  border: 1px solid rgb(var(--v-theme-elevation-2));
  border-radius: var(--app-border-radius);

  &__button {
    flex: 1 1 50%;
    font-weight: var(--font-weight-medium);
    text-transform: uppercase;
    transition: none;

    &--left {
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    }

    &--right {
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
    }

    &--base {
      background-color: transparent;
    }

    & + & {
      margin: 0;
    }
  }

  &__button:not(.order-type-switcher__button--underlined) {
    &:hover {
      background-color: rgb(var(--v-theme-elevation-3)) !important;
    }
  }
}

.close-position-form {
  display: flex;
  height: auto;
  flex: 1 0 auto;
  flex-direction: column;
  color: rgb(var(--v-theme-text-secondary));
  font-size: var(--font-size-sm);

  &__input-wrapper {
    margin-bottom: 10px;
  }

  &__slider {
    margin: 0 8px 24px;
  }

  &__submit {
    margin-top: 16px;
    margin-bottom: 16px;
    font-weight: var(--font-weight-medium);
  }
}
</style>
